// Single source of "today" for every browser-side portal surface.
//
// The portal shows an operator-run schedule, so its day boundary is the
// operator's business day, not UTC and not the viewer's device. Between
// 00:00 and 08:00 CST, UTC is still on the previous date, so a UTC
// "today" files an already-taught class as the parent's next class and
// leaves a day-old assignment unflagged.
//
// The zone is a constant: a browser has no environment to read, and the
// operator's business day does not follow whichever timezone a parent
// happens to be travelling in. The server-side counterpart in
// netlify/functions/portal-data.ts resolves the zone from
// WORK_DASHBOARDS_OPERATOR_TIMEZONE instead — setting that var to any
// other zone requires a paired edit here.
export const OPERATOR_TIMEZONE = 'Asia/Shanghai'

// Built lazily inside a try/catch: api.js imports this module and every
// portal surface imports api.js, so a throw at module evaluation would
// blank the whole portal instead of degrading one date. Tests stubbing
// Intl must vi.resetModules() BEFORE the stub — once any call has built
// the formatter, the cache makes a later stub inert.
let dateParts = null

/**
 * The `YYYY-MM-DD` date in the operator timezone.
 *
 * @param {Date} [now] Instant to resolve; defaults to the system clock.
 * @returns {string}
 */
export function portalToday(now = new Date()) {
  try {
    if (!dateParts) {
      dateParts = new Intl.DateTimeFormat('en-US', {
        timeZone: OPERATOR_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    }
    // Assemble the parts explicitly. No locale guarantees ISO ordering, so
    // reading a formatted string back would be a guess about the runtime's
    // output shape rather than a contract.
    const parts = {}
    for (const part of dateParts.formatToParts(now)) {
      parts[part.type] = part.value
    }
    if (parts.year && parts.month && parts.day) {
      return `${parts.year}-${parts.month}-${parts.day}`
    }
  } catch {
    // fall through to the degraded boundary
  }
  // Degraded fallback when Intl is unavailable or emits unusable parts:
  // the pre-#413 UTC boundary. Wrong by up to 8 hours, but a schedule
  // that self-heals at 08:00 CST beats a portal that renders nothing.
  return now.toISOString().slice(0, 10)
}
